import { Link } from "react-router-dom";
import PageLayout from "@/components/aoria/PageLayout";
import PageHeader from "@/components/aoria/PageHeader";
import Seo from "@/components/aoria/Seo";
import { ARTICLES } from "@/lib/articles";
import { GUIDES, FICHES } from "@/lib/publications";

/**
 * Cinq catégories de ressources d'Aoria, avec un statut à trois niveaux :
 *   - "disponible"   : contenus déjà accessibles
 *   - "premiers"     : premiers contenus annoncés, accès progressif
 *   - "preparation"  : catégorie ouverte mais sans contenu publié pour l'instant
 */
const FAMILIES = [
    {
        num: "01",
        kind: "Articles",
        body: "Analyses brèves et prises de position publiées au fil de l'eau, à l'intersection accessibilité cognitive, numérique et société.",
        status: "disponible",
        statusLabel: "Disponible",
    },
    {
        num: "02",
        kind: "Guides",
        body: "Guides pratiques pour comprendre, expliquer et accompagner, à destination des institutions, des entreprises, des salariés, des indépendants et des acteurs de l'enseignement.",
        status: "premiers",
        statusLabel: "En cours",
    },
    {
        num: "03",
        kind: "Fiches pratiques",
        body: "Fiches courtes et opérationnelles : reformuler une consigne, simplifier une démarche, structurer un courrier, préparer une décision.",
        status: "premiers",
        statusLabel: "En cours",
    },
    {
        num: "04",
        kind: "Études",
        body: "Études plus approfondies, conduites avec des chercheurs et des praticiens, sur des sujets précis : évaluation, formation, accessibilité par défaut.",
        status: "preparation",
        statusLabel: "En préparation",
    },
    {
        num: "05",
        kind: "Prises de position",
        body: "Livre blanc, notes de position, contributions à des consultations publiques, prises de parole d'Aoria dans le débat public.",
        status: "preparation",
        statusLabel: "En préparation",
    },
];

const STATUS_TONES = {
    disponible: "text-[#0D1B2A] font-medium",
    premiers: "text-[#415A77] font-medium",
    preparation: "text-[#778DA9]",
};

export default function Ressources() {
    return (
        <PageLayout testid="ressources-page">
            <Seo
                title="Ressources et publications · Aoria"
                description="Les ressources et publications d'Aoria sur l'accessibilité cognitive à l'ère numérique : articles, guides, fiches pratiques, études et prises de position. Tous nos travaux sont publiés en libre accès, en français."
                path="/ressources"
            />
            <PageHeader
                testid="ressources"
                eyebrow="RESSOURCES"
                title="Ressources"
                italic="et publications"
                lead="Aoria documente progressivement les enjeux de l'accessibilité cognitive et les usages raisonnés de l'intelligence artificielle. Cette rubrique rassemble nos articles, guides, fiches pratiques, études et prises de position. Tous nos travaux sont publiés en libre accès, en français, sans contrepartie commerciale."
                backdrop="network"
            />

            <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
                {/* Five categories, vocabulary stable across the page */}
                <div
                    data-testid="ressources-families"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6"
                >
                    {FAMILIES.map((f) => (
                        <div
                            key={f.kind}
                            data-testid={`ressources-family-${f.num}`}
                            className={`card-aoria ${
                                f.status === "disponible"
                                    ? "card-aoria--accent"
                                    : ""
                            }`}
                        >
                            <div className="flex items-center justify-between mb-5">
                                <span className="editorial-num text-sm">
                                    {f.num}
                                </span>
                                <span
                                    className={`text-[10px] tracking-[0.2em] uppercase ${STATUS_TONES[f.status]}`}
                                >
                                    {f.statusLabel}
                                </span>
                            </div>
                            <h2 className="h3 text-lg md:text-xl text-[#0D1B2A]">
                                {f.kind}
                            </h2>
                            <p className="mt-3 text-[#5C6B7D] text-sm leading-relaxed flex-1">
                                {f.body}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Soft transition from nomenclature to actual content */}
                <div
                    data-testid="ressources-transition"
                    className="mt-16 md:mt-20 max-w-3xl"
                >
                    <div className="flex items-center gap-4 mb-4">
                        <span
                            aria-hidden="true"
                            className="inline-block h-px w-9 bg-[#0D1B2A]/40"
                        />
                        <span className="eyebrow">Premiers contenus publiés</span>
                    </div>
                    <p className="text-[#5C6B7D] leading-relaxed">
                        Les contenus ci-dessous constituent les premières
                        publications d'Aoria. D'autres ressources viendront
                        progressivement compléter chaque catégorie.
                    </p>
                </div>

                {/* Articles list */}
                <section
                    data-testid="ressources-articles-list"
                    className="mt-12 md:mt-16"
                >
                    <div className="flex items-end justify-between border-b border-[#0D1B2A]/15 pb-5 mb-12">
                        <div>
                            <span className="eyebrow">Articles</span>
                            <h2 className="h2 mt-3 text-3xl md:text-4xl text-[#0D1B2A]">
                                Articles récents.
                            </h2>
                        </div>
                        <span className="hidden md:block text-sm text-[#5C6B7D]">
                            {ARTICLES.length} articles publiés
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {ARTICLES.map((a, i) => (
                            <Link
                                key={a.slug}
                                to={`/ressources/${a.slug}`}
                                data-testid={`ressources-card-${i}`}
                                className="card-aoria !p-0 group transition-colors hover:border-[#0D1B2A]"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img
                                        src={a.img}
                                        alt={`Aperçu, ${a.title}`}
                                        width="940"
                                        height="705"
                                        className="absolute inset-0 w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-700 group-hover:scale-[1.03]"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                                <div className="p-7 flex flex-col flex-1">
                                    <div className="flex items-center justify-between eyebrow">
                                        <span>{a.category}</span>
                                        <span className="text-[#5C6B7D]/70 text-[10px] tracking-[0.18em]">
                                            {a.date}
                                        </span>
                                    </div>
                                    <h3 className="h3 text-xl md:text-2xl text-[#0D1B2A] mt-4 leading-snug">
                                        {a.title}
                                    </h3>
                                    <p className="mt-4 text-[#5C6B7D] leading-relaxed text-sm">
                                        {a.excerpt}
                                    </p>
                                    <div className="mt-6 pt-5 border-t border-[#E0E1DD] flex items-center justify-between">
                                        <span className="text-xs text-[#5C6B7D] tracking-wider uppercase">
                                            Lecture · {a.readTime}
                                        </span>
                                        <span className="text-sm text-[#0D1B2A] link-underline">
                                            Lire →
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Guides & Fiches, premières publications hors articles */}
                <section
                    data-testid="ressources-guides-fiches"
                    className="mt-24 md:mt-32"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                        {/* Guides */}
                        <div>
                            <div className="flex items-end justify-between border-b border-[#0D1B2A]/15 pb-5 mb-8">
                                <div>
                                    <span className="eyebrow">Guides</span>
                                    <h2 className="h3 mt-2 text-2xl md:text-3xl text-[#0D1B2A]">
                                        Premier guide annoncé.
                                    </h2>
                                </div>
                                <span className="hidden md:block text-sm text-[#5C6B7D]">
                                    {GUIDES.length} en préparation
                                </span>
                            </div>
                            <div className="space-y-5">
                                {GUIDES.map((g, i) => (
                                    <article
                                        key={g.slug}
                                        data-testid={`ressources-guide-${i}`}
                                        className="card-aoria !min-h-0"
                                    >
                                        <div className="flex items-center justify-between eyebrow">
                                            <span>{g.category}</span>
                                            <span className="text-[#5C6B7D]/70 text-[10px] tracking-[0.18em]">
                                                {g.date}
                                            </span>
                                        </div>
                                        <h3 className="h3 text-lg md:text-xl text-[#0D1B2A] mt-4 leading-snug">
                                            {g.title}
                                        </h3>
                                        <p className="mt-4 text-[#5C6B7D] leading-relaxed text-sm">
                                            {g.excerpt}
                                        </p>
                                        <div className="mt-5 pt-4 border-t border-[#E0E1DD] flex items-center justify-between">
                                            <span className="text-xs text-[#5C6B7D] tracking-wider uppercase">
                                                Lecture · {g.readTime}
                                            </span>
                                            <span className="text-xs text-[#778DA9] tracking-wider uppercase">
                                                Bientôt en ligne
                                            </span>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>

                        {/* Fiches */}
                        <div>
                            <div className="flex items-end justify-between border-b border-[#0D1B2A]/15 pb-5 mb-8">
                                <div>
                                    <span className="eyebrow">Fiches pratiques</span>
                                    <h2 className="h3 mt-2 text-2xl md:text-3xl text-[#0D1B2A]">
                                        Premières fiches annoncées.
                                    </h2>
                                </div>
                                <span className="hidden md:block text-sm text-[#5C6B7D]">
                                    {FICHES.length} en préparation
                                </span>
                            </div>
                            <div className="space-y-5">
                                {FICHES.map((f, i) => (
                                    <article
                                        key={f.slug}
                                        data-testid={`ressources-fiche-${i}`}
                                        className="card-aoria !min-h-0"
                                    >
                                        <div className="flex items-center justify-between eyebrow">
                                            <span>{f.category}</span>
                                            <span className="text-[#5C6B7D]/70 text-[10px] tracking-[0.18em]">
                                                {f.date}
                                            </span>
                                        </div>
                                        <h3 className="h3 text-lg md:text-xl text-[#0D1B2A] mt-4 leading-snug">
                                            {f.title}
                                        </h3>
                                        <p className="mt-4 text-[#5C6B7D] leading-relaxed text-sm">
                                            {f.excerpt}
                                        </p>
                                        <div className="mt-5 pt-4 border-t border-[#E0E1DD] flex items-center justify-between">
                                            <span className="text-xs text-[#5C6B7D] tracking-wider uppercase">
                                                Lecture · {f.readTime}
                                            </span>
                                            <span className="text-xs text-[#778DA9] tracking-wider uppercase">
                                                Bientôt en ligne
                                            </span>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* En préparation, trajectoire éditoriale */}
                <section
                    data-testid="ressources-en-preparation"
                    className="mt-24 md:mt-32 border-t border-[#E0E1DD] pt-16 md:pt-20"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-4">
                            <div className="reveal flex items-center gap-4 mb-6">
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-px w-9 bg-[#0D1B2A]/40"
                                />
                                <span className="eyebrow">En préparation</span>
                            </div>
                            <h2 className="h2 text-2xl md:text-3xl text-[#0D1B2A] leading-tight">
                                La trajectoire éditoriale{" "}
                                <span className="italic text-[#415A77]">
                                    d'Aoria
                                </span>
                                .
                            </h2>
                        </div>
                        <div className="lg:col-span-8 max-w-3xl">
                            <p className="text-[#1B263B] text-base md:text-lg leading-relaxed">
                                Aoria n'a pas vocation à publier tout sur tout.
                                Notre production éditoriale s'organise autour
                                d'un nombre limité de formats, choisis pour
                                leur utilité concrète et leur durabilité.
                            </p>
                            <p className="mt-4 text-[#1B263B] text-base md:text-lg leading-relaxed">
                                Pour être averti des prochaines publications,
                                vous pouvez vous inscrire à la{" "}
                                <Link
                                    to="/#participer"
                                    className="link-underline text-[#0D1B2A]"
                                >
                                    lettre d'information
                                </Link>
                                .
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                        {[
                            {
                                num: "I",
                                title: "Livre blanc Aoria",
                                body: "Document de référence sur l'accessibilité cognitive à l'ère de l'intelligence artificielle : enjeux, leviers et repères pour décideurs publics et privés.",
                                statut: "Version 1 en préparation",
                                to: "/livre-blanc",
                            },
                            {
                                num: "II",
                                title: "Guides pratiques",
                                body: "Démarches d'orientation pour institutions, entreprises, services RH, salariés et indépendants. Premier guide annoncé.",
                                statut: "En cours",
                                to: null,
                            },
                            {
                                num: "III",
                                title: "Fiches pratiques",
                                body: "Repères opérationnels courts : reformuler une consigne, simplifier un courrier, structurer une démarche. Deux fiches annoncées.",
                                statut: "En cours",
                                to: null,
                            },
                            {
                                num: "IV",
                                title: "Contributions extérieures",
                                body: "Tribunes, points de vue et retours d'expérience de chercheurs, praticiens et acteurs de terrain, sous comité éditorial.",
                                statut: "Appel à contributions à venir",
                                to: null,
                            },
                        ].map((item, i) => {
                            const Content = (
                                <>
                                    <div className="flex items-center justify-between mb-6">
                                        <span className="editorial-num text-base">
                                            {item.num}
                                        </span>
                                        <span className="eyebrow text-[#778DA9]">
                                            Format
                                        </span>
                                    </div>
                                    <h3 className="h3 text-lg md:text-xl text-[#0D1B2A] leading-snug">
                                        {item.title}
                                    </h3>
                                    <p className="mt-4 text-[#5C6B7D] text-sm leading-relaxed flex-1">
                                        {item.body}
                                    </p>
                                    <div className="mt-6 pt-5 border-t border-[#E0E1DD] text-xs tracking-[0.16em] uppercase text-[#778DA9]">
                                        {item.statut}
                                    </div>
                                </>
                            );
                            return item.to ? (
                                <Link
                                    key={item.title}
                                    to={item.to}
                                    data-testid={`ressources-trajectoire-${i}`}
                                    className="card-aoria"
                                >
                                    {Content}
                                </Link>
                            ) : (
                                <article
                                    key={item.title}
                                    data-testid={`ressources-trajectoire-${i}`}
                                    className="card-aoria"
                                >
                                    {Content}
                                </article>
                            );
                        })}
                    </div>
                </section>
            </section>
        </PageLayout>
    );
}
